import { useState, useEffect } from "react";
import {
  Card,
  Button,
  message,
  Tag,
  Popconfirm,
  Image,
  Empty,
  Spin,
  Row,
  Col,
  Typography,
  Space,
} from "antd";
import {
  PlusOutlined,
  CheckCircleOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useAuth0 } from "../../../react-auth0-spa";
import { useAppContext } from "../../../context";
import config from "../../../api_config.json";
import sellerCategoriesQuery from "../queries/sellerCategoriesQuery";
import allCategoriesQuery from "../queries/allCategoriesQuery";
import addCategoryToSellerMutation from "../queries/addCategoryToSellerMutation";
import removeCategoryFromSellerMutation from "../queries/removeCategoryFromSellerMutation";

const { Text } = Typography;

const MarketplaceSellerCategories = ({ seller, onUpdate }) => {
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [allCategoriesLoading, setAllCategoriesLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [addingCategory, setAddingCategory] = useState(null);
  const [removingCategory, setRemovingCategory] = useState(null);

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  // Fetch seller's categories
  const fetchSellerCategories = async (isInitial = false) => {
    if (isInitial) setCategoriesLoading(true);
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      const response = await axios.post(
        graphqlUrl,
        {
          query: sellerCategoriesQuery,
          variables: { sellerId: seller.objectId },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const edges = response.data?.marketplace_seller?.categories?.edges || [];
      const sellerCategories = edges.map((edge) => edge.node);
      setCategories(sellerCategories);
    } catch (error) {
      console.error("Error fetching seller categories:", error);
      message.error("Errore durante il caricamento delle categorie");
    } finally {
      if (isInitial) setCategoriesLoading(false);
    }
  };

  // Fetch all available categories
  const fetchAllCategories = async (isInitial = false) => {
    if (isInitial) setAllCategoriesLoading(true);
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      const response = await axios.post(
        graphqlUrl,
        {
          query: allCategoriesQuery,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const edges = response.data?.marketplace_categories?.edges || [];
      const categoriesList = edges.map((edge) => edge.node);
      setAllCategories(categoriesList);
    } catch (error) {
      console.error("Error fetching all categories:", error);
      message.error("Errore durante il caricamento delle categorie disponibili");
    } finally {
      if (isInitial) setAllCategoriesLoading(false);
    }
  };

  // Add category to seller
  const handleAddCategory = async (categoryId) => {
    setAddingCategory(categoryId);
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      await axios.post(
        graphqlUrl,
        {
          query: addCategoryToSellerMutation,
          variables: {
            sellerId: seller.objectId,
            categoryId: categoryId,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      message.success("Categoria aggiunta con successo");
      fetchSellerCategories();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error adding category:", error);
      message.error("Errore durante l'aggiunta della categoria");
    } finally {
      setAddingCategory(null);
    }
  };

  // Remove category from seller
  const handleRemoveCategory = async (categoryId) => {
    setRemovingCategory(categoryId);
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      await axios.post(
        graphqlUrl,
        {
          query: removeCategoryFromSellerMutation,
          variables: {
            sellerId: seller.objectId,
            categoryId: categoryId,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      message.success("Categoria rimossa con successo");
      fetchSellerCategories();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error removing category:", error);
      message.error("Errore durante la rimozione della categoria");
    } finally {
      setRemovingCategory(null);
    }
  };

  useEffect(() => {
    fetchSellerCategories(true);
    fetchAllCategories(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seller.objectId]);

  // Check if a category is already linked
  const isCategoryLinked = (categoryId) => {
    return categories.some((cat) => cat.objectId === categoryId);
  };

  // Filter categories based on seller's b2b/b2c configuration
  const getAvailableCategories = () => {
    return allCategories.filter((cat) => {
      // Category must not be already linked
      if (isCategoryLinked(cat.objectId)) return false;

      // Category must match at least one of seller's modes
      if (seller.b2b && cat.b2b) return true;
      if (seller.b2c && cat.b2c) return true;

      return false;
    });
  };

  const availableCategories = getAvailableCategories();

  return (
    <div>
      {/* Linked Categories */}
      <Card title="Categorie Associate" style={{ marginBottom: 24 }}>
        {categoriesLoading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spin size="large" />
          </div>
        ) : categories.length === 0 ? (
          <Empty
            description="Nessuna categoria associata"
            style={{ padding: "20px" }}
          />
        ) : (
          <Row gutter={[16, 16]}>
            {categories.map((category) => (
              <Col xs={24} sm={12} md={8} lg={6} key={category.objectId}>
                <Card
                  hoverable
                  style={{ textAlign: "center" }}
                  cover={
                    category.icon?.url ? (
                      <div style={{ padding: "20px 20px 0" }}>
                        <Image
                          src={category.icon.url}
                          alt={category.name}
                          height={80}
                          style={{ objectFit: "contain" }}
                          preview={false}
                        />
                      </div>
                    ) : (
                      <div style={{
                        height: 100,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f0f0f0"
                      }}>
                        <Text type="secondary">Nessuna icona</Text>
                      </div>
                    )
                  }
                  actions={[
                    <Popconfirm
                      title="Rimuovere questa categoria?"
                      onConfirm={() => handleRemoveCategory(category.objectId)}
                      okText="Si"
                      cancelText="No"
                      key="remove"
                    >
                      <Button
                        type="text"
                        danger
                        icon={<MinusCircleOutlined />}
                        loading={removingCategory === category.objectId}
                        size="small"
                      >
                        Rimuovi
                      </Button>
                    </Popconfirm>
                  ]}
                >
                  <Card.Meta
                    title={category.name}
                    description={
                      <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        <Tag color="green" icon={<CheckCircleOutlined />}>
                          Associata
                        </Tag>
                        <Space size="small">
                          {category.b2b && <Tag color="blue">B2B</Tag>}
                          {category.b2c && <Tag color="cyan">B2C</Tag>}
                        </Space>
                      </Space>
                    }
                  />
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card>

      {/* Available Categories */}
      <Card title="Categorie Disponibili">
        {allCategoriesLoading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spin size="large" />
          </div>
        ) : allCategories.length === 0 ? (
          <Empty
            description={
              <span>
                Nessuna categoria nel sistema.{" "}
                <a href="/funeralcloud/categories">Crea delle categorie</a> prima di poterle associare.
              </span>
            }
            style={{ padding: "20px" }}
          />
        ) : (
          <>
            <Row gutter={[16, 16]}>
              {availableCategories.map((category) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={category.objectId}>
                    <Card
                      hoverable
                      style={{
                        textAlign: "center",
                        opacity: isCategoryLinked(category.objectId) ? 0.5 : 1
                      }}
                      cover={
                        category.icon?.url ? (
                          <div style={{ padding: "20px 20px 0" }}>
                            <Image
                              src={category.icon.url}
                              alt={category.name}
                              height={80}
                              style={{ objectFit: "contain" }}
                              preview={false}
                            />
                          </div>
                        ) : (
                          <div style={{
                            height: 100,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#f0f0f0"
                          }}>
                            <Text type="secondary">Nessuna icona</Text>
                          </div>
                        )
                      }
                    >
                      <Card.Meta
                        title={category.name}
                        description={
                          <Space direction="vertical" size="small" style={{ width: "100%" }}>
                            <Space size="small">
                              {category.b2b && <Tag color="blue">B2B</Tag>}
                              {category.b2c && <Tag color="cyan">B2C</Tag>}
                            </Space>
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              onClick={() => handleAddCategory(category.objectId)}
                              loading={addingCategory === category.objectId}
                              block
                            >
                              Aggiungi
                            </Button>
                          </Space>
                        }
                      />
                    </Card>
                  </Col>
                ))}
            </Row>

            {availableCategories.length === 0 && (
              <Empty
                description={
                  allCategories.filter(cat => !isCategoryLinked(cat.objectId)).length > 0
                    ? `Nessuna categoria disponibile per questo venditore ${seller.b2b ? 'B2B' : ''} ${seller.b2c ? 'B2C' : ''}`
                    : "Tutte le categorie compatibili sono già associate"
                }
                style={{ padding: "40px" }}
              />
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default MarketplaceSellerCategories;